import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [comic, setComic] = useState<Comic | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const found = allComics.find((c) => c.slug === slug);
    setComic(found || null);
  }, [slug]);

  if (!comic) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
        </div>
        <h1 className="mt-4 font-heading text-xl text-foreground-900">漫画未找到</h1>
        <p className="mt-2 text-sm text-foreground-500">抱歉，您查找的漫画不存在或已下架</p>
        <Link
          to="/category/all"
          className="inline-flex mt-6 px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          浏览全部漫画
        </Link>
      </div>
    );
  }

  const relatedComics = allComics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: comic.category === 'romance' ? '恋爱漫画' : comic.category === 'fantasy' ? '奇幻漫画' : '漫画大全', to: `/category/${comic.category}` },
    { label: comic.title },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2025-01-01',
            dateModified: comic.updatedAt,
            url: `https://www.koreanmanhwa.cn/article/${comic.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.koreanmanhwa.cn/' },
                { '@type': 'ListItem', position: 2, name: comic.category + '漫画', item: `https://www.koreanmanhwa.cn/category/${comic.category}` },
                { '@type': 'ListItem', position: 3, name: comic.title },
              ],
            },
          }),
        }}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={breadcrumbItems} />

        {/* Article Header */}
        <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="flex-shrink-0 w-[200px] md:w-[260px] mx-auto md:mx-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
              <img
                src={comic.coverImage}
                alt={comic.title}
                title={`${comic.title} - 韩国漫画网`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{comic.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
              }`}>
                {comic.status}
              </span>
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70">
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-primary-500">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill"></i>
                  </span>
                  <span className="text-lg font-bold">{comic.rating}</span>
                </div>
                <p className="text-xs text-foreground-500 mt-1">评分</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-foreground-900">
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                </div>
                <p className="text-xs text-foreground-500 mt-1">阅读量</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-foreground-900">{comic.chapters}</div>
                <p className="text-xs text-foreground-500 mt-1">总章节</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <div className="text-sm font-medium text-foreground-900">{comic.author}</div>
                <p className="text-xs text-foreground-500 mt-1">作者</p>
              </div>
            </div>

            <div className="mt-5">
              <h2 className="font-heading text-lg text-foreground-900 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <Link
                to={`/category/${comic.category}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                开始阅读
              </Link>
              <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                <i className="ri-heart-line"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                <i className="ri-share-forward-line"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <section className="mt-10">
          <h2 className="font-heading text-xl text-foreground-900 mb-4">
            <a href="#chapter-list">章节列表</a>
          </h2>
          <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
            <div className="divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
              {comic.chapters_list && comic.chapters_list.length > 0 ? (
                comic.chapters_list.map((ch) => (
                  <Link
                    key={ch.number}
                    to={`/article/${comic.slug}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-900 font-medium">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-500">{ch.date}</span>
                  </Link>
                ))
              ) : (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-foreground-500">章节列表加载中...</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-foreground-900">
                <a href="#related">相关推荐</a>
              </h2>
              <Link
                to={`/category/${comic.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/article/${rc.slug}`}
                  className="group cursor-pointer block"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={rc.coverImage}
                      alt={rc.title}
                      title={`${rc.title} - 韩国漫画网`}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        rc.status === '连载中' ? 'bg-primary-500 text-white' : 'bg-secondary-500 text-white'
                      }`}>
                        {rc.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 leading-tight line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {rc.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}